import React, { useState, useEffect } from 'react';
import { ShoppingCart, Heart, Eye } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useFavorites } from '../../context/FavoritesContext';
import Card from '../common/Card';
import Button from '../common/Button';
import Toast from '../common/Toast';
import api from '../../services/api';

const ProductSection = () => {
    const { addToCart } = useCart();
    const { toggleFavorite, isFavorite } = useFavorites();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const fetchFeaturedProducts = async (isSilent = false) => {
        if (!isSilent) setLoading(true);
        try {
            const response = await api.get('/api/products/');
            // Take the first 3 for the home page showcase
            setProducts(response.data.slice(0, 3));
            setLoading(false);
        } catch (err) {
            console.error('Error fetching featured products:', err);
            if (!isSilent) setError('Impossible de charger les produits vedettes');
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFeaturedProducts();

        // Background polling every 2 minutes
        const interval = setInterval(() => {
            fetchFeaturedProducts(true);
        }, 120000);

        return () => clearInterval(interval);
    }, []);

    const handleAddToCart = (product) => {
        const wasAdded = addToCart(product);
        if (wasAdded) {
            setToastMessage("Produit ajouté au panier !");
        } else {
            setToastMessage("Ce produit est déjà dans votre panier.");
        }
        setShowToast(true);
    };

    return (
        <section id="featured-products" className="py-24 bg-green-50/20">
            <div className="container mx-auto px-6">
                <div className="text-center mb-16">
                    <h2 className="text-sm font-bold tracking-[0.2em] text-green-500 uppercase mb-4">Sélection Exclusive</h2>
                    <h3 className="text-4xl md:text-5xl font-bold text-green-900">Chefs-d'œuvre Vedettes</h3>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="aspect-[4/5] bg-slate-100 rounded-[2rem] animate-pulse" />
                        ))}
                    </div>
                ) : error ? (
                    <div className="text-center py-12">
                        <p className="text-red-500 font-medium">{error}</p>
                        <button onClick={() => window.location.reload()} className="text-brand font-bold underline mt-2">Réessayer</button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-10">
                        {products.map((product) => (
                            <Card key={product.id} className="group h-full flex flex-col">
                                <div className="relative aspect-[4/5] overflow-hidden">
                                    <img
                                        src={product.image.startsWith('http') ? product.image : `${api.defaults.baseURL}${product.image}`}
                                        alt={product.name}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                    />

                                    {/* Stock Badge */}
                                    {product.stock === 0 && (
                                        <div className="absolute top-6 left-6 z-10">
                                            <span className="px-4 py-2 bg-red-500 text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-full shadow-lg">
                                                Épuisé
                                            </span>
                                        </div>
                                    )}

                                    {/* Quick Actions Overlay */}
                                    <div className="absolute inset-0 bg-green-900/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-4">
                                        <Link to={`/product/${product.id}`} className="w-12 h-12 bg-white text-green-900 rounded-full flex items-center justify-center hover:bg-green-900 hover:text-white transition-all transform translate-y-4 group-hover:translate-y-0 duration-500 delay-[0ms] shadow-xl">
                                            <Eye className="w-5 h-5" />
                                        </Link>
                                        <button
                                            onClick={() => toggleFavorite(product)}
                                            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all transform translate-y-4 group-hover:translate-y-0 duration-500 delay-[100ms] shadow-xl ${isFavorite(product.id)
                                                ? 'bg-brand text-white'
                                                : 'bg-white text-green-900 hover:bg-green-900 hover:text-white'
                                                }`}
                                        >
                                            <Heart className={`w-5 h-5 ${isFavorite(product.id) ? 'fill-current' : ''}`} />
                                        </button>
                                        <button
                                            onClick={() => product.stock > 0 && handleAddToCart(product)}
                                            disabled={product.stock === 0}
                                            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all transform translate-y-4 group-hover:translate-y-0 duration-500 delay-[200ms] shadow-xl ${product.stock === 0 ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-white text-green-900 hover:bg-green-900 hover:text-white'}`}
                                        >
                                            <ShoppingCart className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>

                                <div className="p-8 flex flex-col flex-grow">
                                    <div className="mb-6">
                                        <Link to={`/product/${product.id}`}>
                                            <h4 className="text-xl sm:text-2xl font-extrabold text-green-950 group-hover:text-green-600 transition-colors leading-tight mb-3">
                                                {product.name}
                                            </h4>
                                        </Link>
                                        <div className="flex items-center justify-between mb-4">
                                            <p className="text-xl font-black text-brand">
                                                {Math.floor(product.price).toLocaleString('fr-FR')} <span className="text-xs font-bold text-slate-400 uppercase">fcfa</span>
                                            </p>
                                        </div>
                                    </div>

                                    <div className="mt-auto">
                                        <button
                                            onClick={() => product.stock > 0 && handleAddToCart(product)}
                                            disabled={product.stock === 0}
                                            className={`w-full h-14 border-2 rounded-2xl text-sm font-bold transition-all duration-500 flex items-center justify-center gap-3 group/btn ${product.stock === 0
                                                ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                                                : 'bg-green-50 text-green-900 border-green-900/10 hover:bg-green-900 hover:text-white hover:border-green-900'
                                                }`}
                                        >
                                            <ShoppingCart className="w-5 h-5 transition-transform group-hover/btn:scale-110" />
                                            {product.stock === 0 ? 'Rupture de Stock' : 'Ajouter au Panier'}
                                        </button>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}

                <div className="mt-20 text-center">
                    <Button
                        variant="outline"
                        className="px-12"
                        onClick={() => navigate('/categories')}
                    >
                        Voir Tous les Produits
                    </Button>
                </div>
            </div>
            <Toast
                message={toastMessage}
                isOpen={showToast}
                onClose={() => setShowToast(false)}
            />
        </section >
    );
};

export default ProductSection;
